import { Box, Button, Flex, Grid, Image, Spinner, Text } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import React from 'react'
import { ACAO, APAGAR, PropsLayoutTotem, TECLAS } from './tipos'

const MotionFlex = motion(Flex)

/**
 * O arranjo que os totens já usavam: logo, o nome da operação, uma instrução escrita e o
 * teclado com margem nas laterais.
 *
 * A margem é o ponto: o teclado menor abre espaço vertical para o texto de instrução, que
 * é o que resolve a dúvida de quem chega na frente do totem sem saber o que fazer. Quando
 * o teclado ocupa a largura toda não sobra altura para explicar nada.
 */
const LARGURA = 'min(78vw, 52vh)'

const LayoutClassico: React.FC<PropsLayoutTotem> = ({
  logo,
  nomeEvento,
  rotulo,
  instrucao,
  documento,
  mascara,
  erro,
  buscando,
  onTecla,
  cores,
}) => (
  <MotionFlex
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    h="100dvh"
    overflow="hidden"
    bg={cores.primaria}
    color="white"
    direction="column"
    align="center"
    // Centralizado, e não a partir do topo: com o teclado menor sobram ~300px num totem
    // 1080x1920, e presos embaixo eles viram uma faixa vazia que parece defeito. A logo é a
    // única com flex-shrink, então numa tela baixa ela cede antes de qualquer corte.
    justify="center"
    px="4vw"
    pt="2vh"
    pb="1.5vh"
    gap="1.3vh"
  >
    {logo && (
      // Única a encolher se a tela for baixa: o teclado e o visor têm de manter o tamanho
      // do toque, a logo pode ceder.
      <Box
        flex="0 1 auto"
        minH={0}
        maxH="clamp(56px, 13vh, 240px)"
        w="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Image src={logo} alt={nomeEvento} maxH="100%" maxW="min(62vw, 100%)" objectFit="contain" />
      </Box>
    )}

    <Box textAlign="center" flexShrink={0}>
      <Text fontSize="clamp(1.35rem, 5vmin, 3.4rem)" fontWeight="800" letterSpacing="0.05em" lineHeight="1.05">
        AUTOATENDIMENTO
      </Text>
      <Text
        fontSize="clamp(0.65rem, 2.1vmin, 1.4rem)"
        fontWeight="700"
        letterSpacing="0.22em"
        opacity={0.9}
        mt="0.4vh"
      >
        CREDENCIAMENTO
      </Text>
    </Box>

    {/*
      Instrução e erro dividem a mesma caixa, e não uma abaixo da outra: o totem tem altura
      fixa e sem rolagem, então uma faixa que aparece do nada empurraria o teclado para fora
      da tela justamente quando o participante mais precisa dele. Enquanto há erro, a
      instrução não faz falta — ela descreve o passo que acabou de falhar.
    */}
    <Box
      w={LARGURA}
      flexShrink={0}
      minH="clamp(48px, 7.5vh, 130px)"
      display="flex"
      alignItems="center"
      justifyContent="center"
      borderWidth={erro ? 0 : '1px'}
      borderColor="whiteAlpha.700"
      bg={erro ? 'red.600' : 'transparent'}
      borderRadius="md"
      px="3vmin"
      py="1vh"
    >
      <Text
        fontSize="clamp(0.68rem, 2.05vmin, 1.3rem)"
        textAlign="center"
        lineHeight="1.35"
        letterSpacing="0.02em"
        textTransform="uppercase"
        fontWeight={erro ? '700' : '400'}
      >
        {erro || instrucao}
      </Text>
    </Box>

    <Box w={LARGURA} flexShrink={0}>
      <Box bg={cores.secundaria} borderTopRadius="md" py="1.1vh" px="2vmin">
        <Text
          fontSize="clamp(0.9rem, 3vmin, 2rem)"
          fontWeight="800"
          letterSpacing="0.04em"
          textAlign="center"
          textTransform="uppercase"
          lineHeight="1.1"
        >
          Digite seu {rotulo}
        </Text>
      </Box>
      <Box bg="white" borderBottomRadius="md" py="1.2vh" px="3vmin">
        <Text
          fontSize="clamp(1.2rem, 4.4vmin, 2.9rem)"
          fontWeight="bold"
          letterSpacing="wide"
          lineHeight="1.15"
          textAlign="center"
          color={documento ? 'gray.800' : 'gray.400'}
        >
          {documento || mascara}
        </Text>
      </Box>
    </Box>

    <Grid templateColumns="repeat(3, 1fr)" gap="1.4vmin" w={LARGURA} flexShrink={0}>
      {TECLAS.map(t => {
        const acao = t === ACAO
        const apagar = t === APAGAR
        return (
          <Button
            key={t}
            onClick={() => onTecla(t)}
            isDisabled={buscando}
            h="clamp(44px, 9.2vh, 180px)"
            fontSize={acao ? 'clamp(0.85rem, 3vmin, 1.9rem)' : 'clamp(1.25rem, 4.8vmin, 3.1rem)'}
            fontWeight="bold"
            borderRadius="md"
            bg={acao ? cores.secundaria : apagar ? '#F3A9A9' : 'white'}
            color={acao ? 'white' : apagar ? '#7B1D1D' : 'gray.800'}
            _hover={{ bg: acao ? cores.secundaria : apagar ? '#EE9797' : 'gray.100' }}
            _active={{
              bg: acao ? cores.destaque : apagar ? '#E68585' : 'gray.200',
              transform: 'scale(0.97)',
            }}
          >
            {buscando && acao ? <Spinner size="md" /> : acao ? 'Avançar' : t}
          </Button>
        )
      })}
    </Grid>

    <Text fontSize="clamp(0.65rem, 1.9vmin, 1.2rem)" opacity={0.8} textAlign="center" flexShrink={0}>
      Não encontrou seu cadastro? Procure a secretaria.
    </Text>
  </MotionFlex>
)

export default LayoutClassico
